import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { AlertTriangle, Plus, ShieldCheck } from "lucide-react";

import { pageTransition, formatDate } from "@/lib/utils";
import { usePermissions } from "@/hooks/usePermissions";
import { useToast } from "@/hooks/useToast";
import {
  listComplaints,
  submitComplaint,
  updateComplaint,
  getComplaintStats,
} from "@/api/complaints";
import { PageHeader } from "@/components/layout/PageHeader";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { Modal } from "@/components/ui/Modal";
import { DataTable, type Column } from "@/components/shared/DataTable";
import { EmptyState } from "@/components/shared/EmptyState";
import { StatCard } from "@/components/shared/StatCard";
import { SavedViewsMenu } from "@/components/shared/SavedViewsMenu";
import { useSavedViews } from "@/hooks/useSavedViews";
import {
  type Complaint,
  type ComplaintCategory,
  type ComplaintSeverity,
  type ComplaintStatus,
  COMPLAINT_CATEGORY_LABELS,
  COMPLAINT_SEVERITY_LABELS,
  COMPLAINT_STATUS_LABELS,
  SEVERITY_BADGE_VARIANT,
  STATUS_BADGE_VARIANT,
} from "@/types/complaint";
import type { PaginatedResponse } from "@/types/common";

export function ComplaintsPage() {
  const { can } = usePermissions();
  const { addToast } = useToast();
  const navigate = useNavigate();
  const canManage = can("manage_complaints");

  const [data, setData] = useState<PaginatedResponse<Complaint> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [severityFilter, setSeverityFilter] = useState<string>("");

  const filterSnapshot = { statusFilter, severityFilter };
  const savedViews = useSavedViews<typeof filterSnapshot>({
    scope: "complaints",
    filters: filterSnapshot,
    defaultFilters: { statusFilter: "", severityFilter: "" },
    onApply: (f) => {
      setStatusFilter(f.statusFilter);
      setSeverityFilter(f.severityFilter);
      setPage(1);
    },
  });
  const [stats, setStats] = useState<{
    total: number;
    open: number;
    by_severity: Record<string, number>;
  } | null>(null);

  // Submit modal
  const [showSubmit, setShowSubmit] = useState(false);
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [category, setCategory] = useState<ComplaintCategory>("OTHER");
  const [severity, setSeverity] = useState<ComplaintSeverity>("MEDIUM");
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Detail modal
  const [detailOpen, setDetailOpen] = useState(false);
  const [activeComplaint, setActiveComplaint] = useState<Complaint | null>(null);
  const [editStatus, setEditStatus] = useState<ComplaintStatus>("NEW");
  const [editNotes, setEditNotes] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);

  // Anonymous token display (shown ONCE after anon submit)
  const [anonToken, setAnonToken] = useState<string | null>(null);

  const fetchData = useCallback(async () => {
    setIsLoading(true);
    try {
      const result = await listComplaints(
        page,
        20,
        (statusFilter as ComplaintStatus) || undefined,
        undefined,
        (severityFilter as ComplaintSeverity) || undefined,
      );
      setData(result);
    } catch {
      addToast("error", "Failed to load complaints");
    } finally {
      setIsLoading(false);
    }
  }, [page, statusFilter, severityFilter, addToast]);

  const fetchStats = useCallback(async () => {
    if (!canManage) return;
    try {
      const s = await getComplaintStats();
      setStats(s);
    } catch {
      // ignore — stats are non-critical
    }
  }, [canManage]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const resetSubmitForm = () => {
    setTitle("");
    setDescription("");
    setCategory("OTHER");
    setSeverity("MEDIUM");
    setIsAnonymous(false);
  };

  const handleSubmit = async () => {
    if (!title.trim() || !description.trim()) {
      addToast("error", "Title and description are required");
      return;
    }
    setIsSubmitting(true);
    try {
      const result = await submitComplaint({
        title: title.trim(),
        description: description.trim(),
        category,
        severity,
        is_anonymous: isAnonymous,
      });
      addToast("success", "Complaint submitted");
      setShowSubmit(false);
      resetSubmitForm();
      fetchData();
      fetchStats();
      if (result.anonymous_token) {
        setAnonToken(result.anonymous_token);
      }
    } catch {
      addToast("error", "Failed to submit complaint");
    } finally {
      setIsSubmitting(false);
    }
  };

  const openDetail = (c: Complaint) => {
    setActiveComplaint(c);
    setEditStatus(c.status);
    setEditNotes(c.resolution_notes || "");
    setDetailOpen(true);
  };

  const handleUpdate = async () => {
    if (!activeComplaint) return;
    setIsUpdating(true);
    try {
      await updateComplaint(activeComplaint.id, {
        status: editStatus,
        resolution_notes: editNotes || undefined,
      });
      addToast("success", "Complaint updated");
      setDetailOpen(false);
      fetchData();
      fetchStats();
    } catch {
      addToast("error", "Failed to update complaint");
    } finally {
      setIsUpdating(false);
    }
  };

  const columns: Column<Complaint>[] = [
    {
      key: "title",
      header: "Complaint",
      render: (c) => (
        <div>
          <p className="font-medium text-text-primary">{c.title}</p>
          <p className="text-xs text-text-muted mt-0.5">
            {COMPLAINT_CATEGORY_LABELS[c.category]}
            {c.is_anonymous && " • Anonymous"}
          </p>
        </div>
      ),
    },
    {
      key: "submitter",
      header: "Submitted by",
      render: (c) => (
        <span className="text-sm text-text-muted">
          {c.is_anonymous ? "Anonymous" : c.submitter_name || "—"}
        </span>
      ),
    },
    {
      key: "severity",
      header: "Severity",
      render: (c) => (
        <Badge variant={SEVERITY_BADGE_VARIANT[c.severity]}>
          {COMPLAINT_SEVERITY_LABELS[c.severity]}
        </Badge>
      ),
    },
    {
      key: "status",
      header: "Status",
      render: (c) => (
        <Badge variant={STATUS_BADGE_VARIANT[c.status]}>
          {COMPLAINT_STATUS_LABELS[c.status]}
        </Badge>
      ),
    },
    {
      key: "created",
      header: "Submitted",
      render: (c) => (
        <span className="text-xs text-text-muted">{formatDate(c.created_at)}</span>
      ),
    },
  ];

  const categoryOptions = Object.entries(COMPLAINT_CATEGORY_LABELS).map(([v, l]) => ({
    value: v,
    label: l,
  }));

  const severityOptions = Object.entries(COMPLAINT_SEVERITY_LABELS).map(([v, l]) => ({
    value: v,
    label: l,
  }));

  const statusFilterOptions = [
    { value: "", label: "All statuses" },
    ...Object.entries(COMPLAINT_STATUS_LABELS).map(([v, l]) => ({ value: v, label: l })),
  ];
  const severityFilterOptions = [
    { value: "", label: "All severities" },
    ...severityOptions,
  ];

  const editStatusOptions = Object.entries(COMPLAINT_STATUS_LABELS).map(([v, l]) => ({
    value: v,
    label: l,
  }));

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title="Complaints"
        subtitle={
          canManage
            ? `${data?.total ?? 0} complaints — confidential, multi-tenant`
            : "Your submitted reports"
        }
        actions={
          <Button onClick={() => setShowSubmit(true)}>
            <Plus className="h-4 w-4" />
            Submit Complaint
          </Button>
        }
      />

      {canManage && stats && (
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-6">
          <StatCard label="Total" value={stats.total} icon={ShieldCheck} />
          <StatCard label="Open" value={stats.open} icon={AlertTriangle} />
          <StatCard
            label="High / Urgent"
            value={(stats.by_severity?.HIGH ?? 0) + (stats.by_severity?.URGENT ?? 0)}
            icon={AlertTriangle}
          />
          <StatCard
            label="Resolved"
            value={stats.total - stats.open}
            icon={ShieldCheck}
          />
        </div>
      )}

      {canManage && (
        <div className="flex gap-3 mb-6 flex-wrap">
          <Select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            options={statusFilterOptions}
          />
          <Select
            value={severityFilter}
            onChange={(e) => {
              setSeverityFilter(e.target.value);
              setPage(1);
            }}
            options={severityFilterOptions}
          />
          <div className="ml-auto">
            <SavedViewsMenu
              views={savedViews.views}
              activeView={savedViews.activeView}
              hasUnsavedChanges={savedViews.hasUnsavedChanges}
              isDefault={savedViews.isDefault}
              onApply={savedViews.applyView}
              onSave={savedViews.saveView}
              onDelete={savedViews.deleteView}
              onRename={savedViews.renameView}
              onUpdateActive={savedViews.updateActiveView}
              onClear={savedViews.clear}
            />
          </div>
        </div>
      )}

      <div className="bg-surface rounded-xl border border-border">
        <DataTable
          columns={columns}
          data={data?.items ?? []}
          keyExtractor={(c) => c.id}
          isLoading={isLoading}
          page={page}
          totalPages={data?.total_pages ?? 1}
          onPageChange={setPage}
          onRowClick={(c) =>
            canManage ? navigate(`/complaints/${c.id}`) : openDetail(c)
          }
          emptyState={
            canManage ? (
              statusFilter || severityFilter ? (
                <EmptyState
                  icon={ShieldCheck}
                  title="No complaints match these filters"
                  description="Try a different status or severity, or clear the filters."
                  action={{
                    label: "Clear filters",
                    onClick: () => {
                      setStatusFilter("");
                      setSeverityFilter("");
                      setPage(1);
                    },
                  }}
                />
              ) : (
                <EmptyState
                  icon={ShieldCheck}
                  title="Nothing reported yet"
                  description="A quiet queue is a good sign — reports will show up here the moment anyone on your team files one."
                />
              )
            ) : (
              <EmptyState
                icon={ShieldCheck}
                title="No reports yet"
                description="If something needs attention, submit a report confidentially. You can file it under your name or anonymously."
                action={{
                  label: "Submit Complaint",
                  onClick: () => setShowSubmit(true),
                }}
              />
            )
          }
        />
      </div>

      {/* Submit modal */}
      <Modal
        isOpen={showSubmit}
        onClose={() => {
          setShowSubmit(false);
          resetSubmitForm();
        }}
        title="Submit a complaint"
        size="xl"
      >
        <div className="space-y-4">
          <p className="text-xs text-text-muted">
            Reports are confidential. Submit anonymously if you'd prefer not to identify
            yourself — HR will still be able to investigate.
          </p>
          <Input
            label="Title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="Brief summary"
          />
          <div>
            <label className="block text-sm font-medium text-text-primary mb-1">
              Description
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={5}
              className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent"
              placeholder="What happened? Include dates, people involved (if you wish), and any context."
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="Category"
              value={category}
              onChange={(e) => setCategory(e.target.value as ComplaintCategory)}
              options={categoryOptions}
            />
            <Select
              label="Severity"
              value={severity}
              onChange={(e) => setSeverity(e.target.value as ComplaintSeverity)}
              options={severityOptions}
            />
          </div>
          <label className="flex items-center gap-2 text-sm text-text-primary cursor-pointer">
            <input
              type="checkbox"
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e.target.checked)}
              className="rounded border-border text-accent focus:ring-accent"
            />
            Submit anonymously
          </label>
          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="secondary"
              onClick={() => {
                setShowSubmit(false);
                resetSubmitForm();
              }}
            >
              Cancel
            </Button>
            <Button onClick={handleSubmit} isLoading={isSubmitting}>
              Submit
            </Button>
          </div>
        </div>
      </Modal>

      {/* Detail modal */}
      <Modal
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        title={activeComplaint?.title || "Complaint"}
        size="xl"
      >
        {activeComplaint && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-2">
              <Badge variant={STATUS_BADGE_VARIANT[activeComplaint.status]}>
                {COMPLAINT_STATUS_LABELS[activeComplaint.status]}
              </Badge>
              <Badge variant={SEVERITY_BADGE_VARIANT[activeComplaint.severity]}>
                {COMPLAINT_SEVERITY_LABELS[activeComplaint.severity]}
              </Badge>
              <Badge variant="default">
                {COMPLAINT_CATEGORY_LABELS[activeComplaint.category]}
              </Badge>
              {activeComplaint.is_anonymous && <Badge variant="info">Anonymous</Badge>}
            </div>

            <div>
              <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-1">
                Description
              </p>
              <p className="text-sm text-text-primary whitespace-pre-wrap">
                {activeComplaint.description}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-xs text-text-muted">Submitted by</p>
                <p className="text-text-primary">
                  {activeComplaint.is_anonymous
                    ? "Anonymous"
                    : activeComplaint.submitter_name || "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-text-muted">Submitted</p>
                <p className="text-text-primary">
                  {formatDate(activeComplaint.created_at)}
                </p>
              </div>
              {activeComplaint.assignee_name && (
                <div>
                  <p className="text-xs text-text-muted">Assigned to</p>
                  <p className="text-text-primary">{activeComplaint.assignee_name}</p>
                </div>
              )}
              {activeComplaint.resolved_at && (
                <div>
                  <p className="text-xs text-text-muted">Resolved</p>
                  <p className="text-text-primary">
                    {formatDate(activeComplaint.resolved_at)}
                  </p>
                </div>
              )}
            </div>

            {canManage && (
              <div className="border-t border-border pt-4 space-y-3">
                <Select
                  label="Update status"
                  value={editStatus}
                  onChange={(e) => setEditStatus(e.target.value as ComplaintStatus)}
                  options={editStatusOptions}
                />
                <div>
                  <label className="block text-sm font-medium text-text-primary mb-1">
                    Resolution notes
                  </label>
                  <textarea
                    value={editNotes}
                    onChange={(e) => setEditNotes(e.target.value)}
                    rows={3}
                    className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-2 focus:ring-accent/50 focus:border-accent"
                    placeholder="Investigation findings, actions taken..."
                  />
                </div>
                <div className="flex justify-end">
                  <Button onClick={handleUpdate} isLoading={isUpdating}>
                    Save changes
                  </Button>
                </div>
              </div>
            )}

            {!canManage && activeComplaint.resolution_notes && (
              <div>
                <p className="text-xs font-medium text-text-muted uppercase tracking-wider mb-1">
                  Resolution
                </p>
                <p className="text-sm text-text-primary whitespace-pre-wrap">
                  {activeComplaint.resolution_notes}
                </p>
              </div>
            )}
          </div>
        )}
      </Modal>

      {/* Anonymous tracking token modal — shown ONCE */}
      <Modal
        isOpen={!!anonToken}
        onClose={() => setAnonToken(null)}
        title="Save your tracking link"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-sm text-text-primary">
            Your report was submitted anonymously. Save the tracking token below
            to check on its status later at{" "}
            <code className="text-xs">/anon-status/&lt;org&gt;/&lt;token&gt;</code>
            . This is the only time we will show it — we have no way to recover
            it for you.
          </p>
          {anonToken && (
            <div className="rounded-lg border border-border bg-background p-3 break-all text-xs font-mono text-text-primary">
              {anonToken}
            </div>
          )}
          <div className="flex justify-end gap-3 pt-2">
            <Button
              variant="secondary"
              onClick={() => {
                if (anonToken) {
                  navigator.clipboard
                    ?.writeText(anonToken)
                    .catch(() => undefined);
                  addToast("success", "Token copied");
                }
              }}
            >
              Copy token
            </Button>
            <Button onClick={() => setAnonToken(null)}>I saved it</Button>
          </div>
        </div>
      </Modal>
    </motion.div>
  );
}
